/**
 * Smart Schedule Editor
 *
 * Intelligent schedule component that groups days with identical hours.
 * Combines Store Hours and Liquor Cutoff into a unified "cockpit-style" control panel.
 *
 * Features:
 * - Auto-groups days (Mon-Fri as "Weekdays", Sat-Sun as "Weekends")
 * - Merged controls: Store Hours (Open/Close) + Liquor Cutoff
 * - "Expand/Customize" to break groups for individual day editing
 * - Inheritance visualization (Global vs Custom)
 *
 * @module StoreCommandCenter/components/ui
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Button,
  TextField,
  Switch,
  Collapse,
} from '@mui/material';
import {
  Schedule as ClockIcon,
  WineBar as LiquorIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Tune as CustomizeIcon,
  Public as GlobalIcon,
  Store as StoreIcon,
  Warning as WarningIcon,
  ContentCopy as CopyIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

// ============================================================================
// Types
// ============================================================================

export interface DaySchedule {
  openTime: string;
  closeTime: string;
  liquorCutoff: string | null;
  liquorStartTime: string | null;
}

export interface ScheduleData {
  [dayIndex: number]: DaySchedule;
}

interface SmartScheduleEditorProps {
  /** Current schedule data (0-6 for Sun-Sat) */
  schedule: ScheduleData;
  /** Global default schedule for inheritance display */
  globalSchedule: ScheduleData;
  /** Whether to use global defaults */
  useGlobalDefaults: boolean;
  /** Callback when schedule changes */
  onScheduleChange: (schedule: ScheduleData) => void;
  /** Callback when global defaults toggle changes */
  onUseGlobalDefaultsChange: (useDefaults: boolean) => void;
  /** Whether the component is in a saving state */
  saving?: boolean;
  /** Callback when user clicks "Manage System Defaults" link */
  onManageDefaults?: () => void;
  /** Whether this is the System Defaults page (hides override toggle) */
  isSystemDefaults?: boolean;
  /** Inside a titled section card: skip the editor's own icon + title (the card shows them) */
  embedded?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const colors = {
  bgDeepest: 'var(--canvas)',
  bgCard: 'var(--panel)',
  bgElevated: 'var(--raised)',
  bgRecessed: 'var(--sunk)',
  bgInput: 'var(--sunk)',
  textPrimary: 'var(--tx)',
  textSecondary: 'var(--tx2)',
  textMuted: 'var(--tx3)',
  textGhosted: '#4A4A4A',
  border: 'var(--line-soft)',
  borderActive: '#FF003C',
  successGlow: '#00FF88',
  errorGlow: '#FF003C',
  warning: '#FFB800',
  info: '#00B4FF',
};

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Default groupings - Weekdays (Mon-Fri), Saturday, Sunday separate
const DEFAULT_GROUPS = [
  { name: 'Weekdays', days: [1, 2, 3, 4, 5], icon: '📅' },
  { name: 'Saturday', days: [6], icon: '🌴' },
  { name: 'Sunday', days: [0], icon: '☀️' },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if two day schedules are identical
 */
function schedulesMatch(a: DaySchedule, b: DaySchedule): boolean {
  return a.openTime === b.openTime &&
    a.closeTime === b.closeTime &&
    a.liquorCutoff === b.liquorCutoff &&
    a.liquorStartTime === b.liquorStartTime;
}

/**
 * Group days with identical schedules
 */
function groupDaysBySchedule(schedule: ScheduleData): { days: number[]; schedule: DaySchedule }[] {
  const emptySchedule: DaySchedule = { openTime: '', closeTime: '', liquorCutoff: null, liquorStartTime: null };
  const groups: { days: number[]; schedule: DaySchedule }[] = [];
  const assigned = new Set<number>();

  // Handle empty schedule
  if (!schedule || Object.keys(schedule).length === 0) {
    return [0, 1, 2, 3, 4, 5, 6].map(d => ({ days: [d], schedule: emptySchedule }));
  }

  // Try to match default groupings first (Mon-Fri, Sat-Sun)
  for (const defaultGroup of DEFAULT_GROUPS) {
    const daysInGroup = defaultGroup.days.filter(d => !assigned.has(d) && schedule[d]);
    if (daysInGroup.length === 0) continue;

    const firstSchedule = schedule[daysInGroup[0]] || emptySchedule;
    const allMatch = daysInGroup.every(d => schedule[d] && schedulesMatch(schedule[d], firstSchedule));

    if (allMatch && daysInGroup.length > 1) {
      groups.push({ days: daysInGroup, schedule: firstSchedule });
      daysInGroup.forEach(d => assigned.add(d));
    }
  }

  // Add remaining days individually
  for (let day = 0; day < 7; day++) {
    if (!assigned.has(day)) {
      groups.push({ days: [day], schedule: schedule[day] || emptySchedule });
    }
  }

  // Sort groups by first day
  groups.sort((a, b) => Math.min(...a.days) - Math.min(...b.days));

  return groups;
}

/**
 * Format day range for display
 */
function formatDayRange(days: number[]): string {
  if (days.length === 1) return DAYS[days[0]];
  if (days.length === 7) return 'Every Day';

  // Check for weekdays
  if (days.length === 5 && [1, 2, 3, 4, 5].every(d => days.includes(d))) {
    return 'Weekdays (Mon-Fri)';
  }

  // Note: Saturday and Sunday are now separate, not grouped as "Weekends"
  // Keeping this for backward compatibility if they ever are grouped
  if (days.length === 2 && days.includes(0) && days.includes(6)) {
    return 'Weekends (Sat-Sun)';
  }

  // Consecutive days
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1)) {
    return `${SHORT_DAYS[sorted[0]]}-${SHORT_DAYS[sorted[sorted.length - 1]]}`;
  }

  // Non-consecutive
  return sorted.map(d => SHORT_DAYS[d]).join(', ');
}

// ============================================================================
// Sub-components
// ============================================================================

interface TimeInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  ghosted?: boolean;
  accentColor?: string;
}

function TimeInput({ label, value, onChange, disabled, ghosted, accentColor = colors.info }: TimeInputProps) {
  return (
    <Box sx={{ flex: 1 }}>
      <Typography
        variant="caption"
        sx={{
          color: ghosted ? colors.textGhosted : colors.textMuted,
          fontSize: '0.65rem',
          fontWeight: 600,
          letterSpacing: '0.05em',
          display: 'block',
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <TextField
        type="time"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || ghosted}
        size="small"
        InputLabelProps={{ shrink: true }}
        sx={{
          width: '100%',
          '& .MuiOutlinedInput-root': {
            bgcolor: ghosted ? 'transparent' : colors.bgInput,
            color: ghosted ? colors.textGhosted : colors.textPrimary,
            fontFamily: 'monospace',
            fontWeight: 600,
            fontSize: '0.95rem',
            '& fieldset': {
              borderColor: ghosted ? 'var(--line-soft)' : colors.border,
              borderWidth: ghosted ? 1 : 2,
            },
            '&:hover fieldset': {
              borderColor: ghosted ? 'var(--line-soft)' : accentColor,
            },
            '&.Mui-focused fieldset': {
              borderColor: accentColor,
              boxShadow: `0 0 8px ${accentColor}30`,
            },
          },
          '& .MuiInputBase-input': {
            py: 1,
            px: 1.5,
          },
        }}
      />
    </Box>
  );
}

interface ScheduleRowProps {
  days: number[];
  schedule: DaySchedule;
  globalSchedule: DaySchedule;
  isGhosted: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onChange: (field: keyof DaySchedule, value: string) => void;
  onCopyToAll: () => void;
  canExpand: boolean;
  /** When true, days in this group have differing schedules */
  hasMismatch?: boolean;
}

function ScheduleRow({
  days,
  schedule,
  globalSchedule,
  isGhosted,
  isExpanded,
  onToggleExpand,
  onChange,
  onCopyToAll,
  canExpand,
  hasMismatch = false,
}: ScheduleRowProps) {
  const displaySchedule = isGhosted ? globalSchedule : schedule;

  return (
    <Paper
      sx={{
        bgcolor: isGhosted ? colors.bgRecessed : colors.bgElevated,
        borderRadius: 2,
        border: `1px solid ${isGhosted ? colors.border : `${colors.info}30`}`,
        overflow: 'hidden',
        mb: 1.5,
        opacity: isGhosted ? 0.7 : 1,
        transition: 'all 0.3s',
        '&:hover': {
          borderColor: isGhosted ? colors.border : colors.info,
          boxShadow: isGhosted ? 'none' : `0 0 15px ${colors.info}10`,
        },
      }}
    >
      {/* Row Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          bgcolor: isGhosted ? 'transparent' : `${colors.info}05`,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Day indicator pills */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[0, 1, 2, 3, 4, 5, 6].map((d) => (
              <Box
                key={d}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: days.includes(d)
                    ? isGhosted
                      ? 'var(--line-soft)'
                      : `${colors.info}20`
                    : 'transparent',
                  border: `1px solid ${days.includes(d)
                    ? isGhosted
                      ? colors.textMuted
                      : colors.info
                    : colors.border
                    }`,
                  color: days.includes(d)
                    ? isGhosted
                      ? colors.textMuted
                      : colors.info
                    : colors.textGhosted,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                }}
              >
                {SHORT_DAYS[d].charAt(0)}
              </Box>
            ))}
          </Box>

          <Typography
            variant="subtitle2"
            fontWeight={600}
            sx={{ color: isGhosted ? colors.textMuted : colors.textPrimary }}
          >
            {formatDayRange(days)}
          </Typography>

          {isGhosted && (
            <Chip
              icon={<GlobalIcon sx={{ fontSize: 12 }} />}
              label="GLOBAL"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6rem',
                bgcolor: `${colors.info}15`,
                color: colors.info,
                border: `1px solid ${colors.info}30`,
                '& .MuiChip-icon': { color: colors.info },
              }}
            />
          )}
          {hasMismatch && !isExpanded && (
            <Tooltip title="Days in this group have different hours. Editing will sync all days. Use 'Customize Individual Days' to edit separately.">
              <Chip
                icon={<WarningIcon sx={{ fontSize: 12 }} />}
                label="Mixed"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6rem',
                  bgcolor: `${colors.warning}15`,
                  color: colors.warning,
                  border: `1px solid ${colors.warning}30`,
                  '& .MuiChip-icon': { color: colors.warning },
                  cursor: 'help',
                }}
              />
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {!isGhosted && days.length > 0 && (
            <Tooltip title="Copy to all days">
              <IconButton
                size="small"
                onClick={onCopyToAll}
                sx={{ color: colors.textMuted, '&:hover': { color: colors.info } }}
              >
                <CopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {canExpand && (
            <Tooltip title={isExpanded ? 'Collapse' : 'Expand for individual days'}>
              <IconButton
                size="small"
                onClick={onToggleExpand}
                sx={{ color: colors.textMuted, '&:hover': { color: colors.info } }}
              >
                {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Time Controls */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Store Hours */}
          <Box sx={{ minWidth: 280, maxWidth: 320 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ClockIcon sx={{ fontSize: 16, color: isGhosted ? colors.textGhosted : colors.info }} />
              <Typography
                variant="caption"
                sx={{
                  color: isGhosted ? colors.textGhosted : colors.textSecondary,
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                }}
              >
                STORE HOURS
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TimeInput
                label="OPENS"
                value={displaySchedule.openTime}
                onChange={(v) => onChange('openTime', v)}
                ghosted={isGhosted}
                accentColor={colors.successGlow}
              />
              <TimeInput
                label="CLOSES"
                value={displaySchedule.closeTime}
                onChange={(v) => onChange('closeTime', v)}
                ghosted={isGhosted}
                accentColor={colors.errorGlow}
              />
            </Box>
          </Box>

          {/* Divider */}
          <Box
            sx={{
              width: 1,
              bgcolor: colors.border,
              alignSelf: 'stretch',
              minHeight: 60,
            }}
          />

          {/* Restricted Start Time */}
          <Box sx={{ minWidth: 140, maxWidth: 180 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LiquorIcon sx={{ fontSize: 16, color: isGhosted ? colors.textGhosted : colors.warning }} />
              <Typography
                variant="caption"
                sx={{
                  color: isGhosted ? colors.textGhosted : colors.textSecondary,
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                }}
              >
                RESTRICTED START
              </Typography>
            </Box>
            <TimeInput
              label="START SALES"
              value={displaySchedule.liquorStartTime || ''}
              onChange={(v) => onChange('liquorStartTime', v)}
              ghosted={isGhosted}
              accentColor={colors.warning}
            />
            {!displaySchedule.liquorStartTime && (
              <Typography variant="caption" sx={{ color: colors.textGhosted, fontSize: '0.6rem', mt: 0.5, display: 'block' }}>
                No start restriction
              </Typography>
            )}
          </Box>

          {/* Liquor Cutoff */}
          <Box sx={{ minWidth: 140, maxWidth: 180 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LiquorIcon sx={{ fontSize: 16, color: isGhosted ? colors.textGhosted : colors.warning }} />
              <Typography
                variant="caption"
                sx={{
                  color: isGhosted ? colors.textGhosted : colors.textSecondary,
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                }}
              >
                LIQUOR CUTOFF
              </Typography>
            </Box>
            <TimeInput
              label="STOP SALES"
              value={displaySchedule.liquorCutoff || ''}
              onChange={(v) => onChange('liquorCutoff', v)}
              ghosted={isGhosted}
              accentColor={colors.warning}
            />
            {!displaySchedule.liquorCutoff && (
              <Typography variant="caption" sx={{ color: colors.textGhosted, fontSize: '0.6rem', mt: 0.5, display: 'block' }}>
                No cutoff (uses store close)
              </Typography>
            )}
          </Box>

          {/* Spacer to push content left */}
          <Box sx={{ flex: 1 }} />
        </Box>
      </Box>
    </Paper>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SmartScheduleEditor({
  schedule,
  globalSchedule,
  useGlobalDefaults,
  onScheduleChange,
  onUseGlobalDefaultsChange,
  saving = false,
  onManageDefaults,
  isSystemDefaults = false,
  embedded = false,
}: SmartScheduleEditorProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Default empty schedule for safety
  const emptyDaySchedule: DaySchedule = { openTime: '', closeTime: '', liquorCutoff: null, liquorStartTime: null };

  // Compute grouped schedule with null safety
  const groups = useMemo(() => {
    const displaySchedule = useGlobalDefaults ? globalSchedule : schedule;
    // Ensure we have valid schedule data
    if (!displaySchedule || Object.keys(displaySchedule).length === 0) {
      return DEFAULT_GROUPS.map(g => ({ days: g.days, schedule: emptyDaySchedule }));
    }
    if (isExpanded) {
      return [0, 1, 2, 3, 4, 5, 6].map(d => ({ days: [d], schedule: displaySchedule[d] || emptyDaySchedule }));
    }
    // Default: always show 3 groups (Sunday, Weekdays, Saturday)
    // Uses first day in each group as the representative display value
    return DEFAULT_GROUPS.map(g => ({
      days: g.days,
      schedule: displaySchedule[g.days[0]] || emptyDaySchedule,
    }));
  }, [schedule, globalSchedule, useGlobalDefaults, isExpanded]);

  // Health check
  const healthStatus = useMemo(() => {
    const issues: string[] = [];
    const displaySchedule = useGlobalDefaults ? globalSchedule : schedule;

    for (let day = 0; day < 7; day++) {
      if (!displaySchedule[day]?.openTime || !displaySchedule[day]?.closeTime) {
        issues.push(`${DAYS[day]} missing hours`);
      }
    }

    return {
      isHealthy: issues.length === 0,
      issues,
    };
  }, [schedule, globalSchedule, useGlobalDefaults]);

  const handleScheduleChange = (days: number[], field: keyof DaySchedule, value: string) => {
    const newSchedule = { ...schedule };
    days.forEach((day) => {
      newSchedule[day] = {
        ...newSchedule[day],
        [field]: value || null,
      };
    });
    onScheduleChange(newSchedule);
  };

  const handleCopyToAll = (sourceDays: number[]) => {
    if (sourceDays.length === 0) return;
    const sourceSchedule = schedule[sourceDays[0]];
    const newSchedule: ScheduleData = {};
    for (let day = 0; day < 7; day++) {
      newSchedule[day] = { ...sourceSchedule };
    }
    onScheduleChange(newSchedule);
  };

  return (
    <Box>
      {/* Header with Override Toggle */}
      <Paper
        sx={{
          p: 2,
          mb: 2,
          bgcolor: colors.bgElevated,
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
        }}
      >
        {(!embedded || !healthStatus.isHealthy) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: embedded ? 'flex-end' : 'space-between' }}>
          {!embedded && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${colors.info}15`,
                border: `1px solid ${colors.info}30`,
              }}
            >
              <ClockIcon sx={{ fontSize: 24, color: colors.info }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: colors.textPrimary }}>
                Store Schedule
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                Operating hours and liquor cutoff times
              </Typography>
            </Box>
          </Box>
          )}

          {/* Health Status */}
          {!healthStatus.isHealthy && (
            <Tooltip title={healthStatus.issues.join(', ')}>
              <Chip
                icon={<WarningIcon sx={{ fontSize: 14 }} />}
                label={`${healthStatus.issues.length} issue${healthStatus.issues.length > 1 ? 's' : ''}`}
                size="small"
                sx={{
                  bgcolor: `${colors.warning}15`,
                  color: colors.warning,
                  border: `1px solid ${colors.warning}30`,
                  '& .MuiChip-icon': { color: colors.warning },
                }}
              />
            </Tooltip>
          )}
        </Box>
        )}

        {/* Override Toggle - hidden for System Defaults page */}
        {!isSystemDefaults && (
          <Box
            sx={{
              mt: embedded && healthStatus.isHealthy ? 0 : 2,
              p: 2,
              borderRadius: 2,
              bgcolor: useGlobalDefaults ? `${colors.info}08` : `${colors.successGlow}08`,
              border: `1px solid ${useGlobalDefaults ? `${colors.info}30` : `${colors.successGlow}30`}`,
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                bgcolor: useGlobalDefaults ? `${colors.info}12` : `${colors.successGlow}12`,
              },
            }}
            onClick={() => onUseGlobalDefaultsChange(!useGlobalDefaults)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {useGlobalDefaults ? (
                  <GlobalIcon sx={{ fontSize: 20, color: colors.info }} />
                ) : (
                  <StoreIcon sx={{ fontSize: 20, color: colors.successGlow }} />
                )}
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: colors.textPrimary }}>
                    {useGlobalDefaults ? 'Using Global Defaults' : 'Custom Store Schedule'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    {useGlobalDefaults
                      ? 'This store inherits schedule from system defaults'
                      : 'This store has custom operating hours'}
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={!useGlobalDefaults}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.successGlow,
                    '& + .MuiSwitch-track': { bgcolor: `${colors.successGlow}60` },
                  },
                  '& .MuiSwitch-track': { bgcolor: colors.border },
                }}
              />
            </Box>
          </Box>
        )}
      </Paper>

      {/* When using global defaults - show READ-ONLY summary of inherited values (not shown for System Defaults page) */}
      <Collapse in={useGlobalDefaults && !isSystemDefaults}>
        <Paper
          sx={{
            p: 2.5,
            mb: 2,
            bgcolor: `${colors.info}05`,
            borderRadius: 2,
            border: `1px solid ${colors.info}30`,
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <GlobalIcon sx={{ fontSize: 20, color: colors.info }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: colors.textPrimary }}>
                Inherited from System Defaults
              </Typography>
            </Box>
            {onManageDefaults && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
                onClick={onManageDefaults}
                sx={{
                  color: colors.info,
                  borderColor: `${colors.info}50`,
                  fontSize: '0.75rem',
                  py: 0.5,
                  '&:hover': {
                    borderColor: colors.info,
                    bgcolor: `${colors.info}10`,
                  },
                }}
              >
                Edit Defaults
              </Button>
            )}
          </Box>

          {/* Schedule Summary - Read Only */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {groupDaysBySchedule(globalSchedule).map((group) => (
              <Box
                key={group.days.join('-')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  bgcolor: colors.bgRecessed,
                  borderRadius: 1.5,
                  border: `1px solid ${colors.border}`,
                }}
              >
                {/* Day indicator */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 180 }}>
                  <Box sx={{ display: 'flex', gap: 0.3 }}>
                    {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                      <Box
                        key={d}
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: group.days.includes(d) ? `${colors.info}20` : 'transparent',
                          border: `1px solid ${group.days.includes(d) ? colors.info : 'var(--line-soft)'}`,
                          color: group.days.includes(d) ? colors.info : colors.textGhosted,
                          fontSize: '0.55rem',
                          fontWeight: 600,
                        }}
                      >
                        {SHORT_DAYS[d].charAt(0)}
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
                    {formatDayRange(group.days)}
                  </Typography>
                </Box>

                {/* Store Hours */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClockIcon sx={{ fontSize: 14, color: colors.info }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: group.schedule?.openTime ? colors.textPrimary : colors.textMuted,
                        fontFamily: 'monospace',
                        fontWeight: 600,
                      }}
                    >
                      {group.schedule?.openTime && group.schedule?.closeTime
                        ? `${group.schedule.openTime} - ${group.schedule.closeTime}`
                        : 'Closed'}
                    </Typography>
                  </Box>

                  {/* Restricted Hours (Start Time & Cutoff) */}
                  {(group.schedule?.liquorStartTime || group.schedule?.liquorCutoff) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LiquorIcon sx={{ fontSize: 14, color: colors.warning }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.warning,
                          fontFamily: 'monospace',
                          fontWeight: 600,
                        }}
                      >
                        {group.schedule.liquorStartTime && group.schedule.liquorCutoff
                          ? `${group.schedule.liquorStartTime} - ${group.schedule.liquorCutoff}`
                          : group.schedule.liquorStartTime
                            ? `from ${group.schedule.liquorStartTime}`
                            : `until ${group.schedule.liquorCutoff}`}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
          </Box>

          {/* Footer hint */}
          <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mt: 2, textAlign: 'center' }}>
            Toggle "Custom Store Schedule" above to override these hours for this store only
          </Typography>
        </Paper>
      </Collapse>

      {/* Custom Schedule Editor - shown when NOT using global defaults OR on System Defaults page */}
      <Collapse in={!useGlobalDefaults || isSystemDefaults}>
        {/* Bulk Actions Bar */}
        <Paper
          sx={{
            p: 2,
            mb: 2,
            bgcolor: colors.bgElevated,
            borderRadius: 2,
            border: `1px solid ${colors.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600 }}>
              Bulk Actions:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Tooltip title="Apply current weekday hours (Mon-Fri) to Saturday and Sunday">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const weekdaySchedule = schedule[1]; // Monday
                    if (weekdaySchedule) {
                      const newSchedule = { ...schedule };
                      newSchedule[0] = { ...weekdaySchedule }; // Sunday
                      newSchedule[6] = { ...weekdaySchedule }; // Saturday
                      onScheduleChange(newSchedule);
                    }
                  }}
                  sx={{
                    color: colors.textSecondary,
                    borderColor: colors.border,
                    fontSize: '0.75rem',
                    '&:hover': { borderColor: colors.info, color: colors.info },
                  }}
                >
                  Copy Weekdays → Weekend
                </Button>
              </Tooltip>
              <Tooltip title="Set all days to 08:00 - 22:00">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const defaultHours: DaySchedule = { openTime: '08:00', closeTime: '22:00', liquorCutoff: null, liquorStartTime: null };
                    const newSchedule: ScheduleData = {};
                    for (let day = 0; day < 7; day++) {
                      newSchedule[day] = { ...defaultHours };
                    }
                    onScheduleChange(newSchedule);
                  }}
                  sx={{
                    color: colors.textSecondary,
                    borderColor: colors.border,
                    fontSize: '0.75rem',
                    '&:hover': { borderColor: colors.successGlow, color: colors.successGlow },
                  }}
                >
                  Reset to Standard Hours
                </Button>
              </Tooltip>
              <Tooltip title="Copy hours from first day to all days">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => handleCopyToAll([1])}
                  sx={{
                    color: colors.textSecondary,
                    borderColor: colors.border,
                    fontSize: '0.75rem',
                    '&:hover': { borderColor: colors.warning, color: colors.warning },
                  }}
                >
                  Apply to All Days
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Paper>

        {/* View Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            size="small"
            startIcon={isExpanded ? <CollapseIcon /> : <CustomizeIcon />}
            onClick={() => setIsExpanded(!isExpanded)}
            sx={{
              color: colors.textSecondary,
              borderColor: colors.border,
              '&:hover': { borderColor: colors.textPrimary, color: colors.textPrimary },
            }}
            variant="outlined"
          >
            {isExpanded ? 'Smart Group View' : 'Customize Individual Days'}
          </Button>
        </Box>

        {/* Schedule Rows */}
        {groups.map((group, idx) => {
          // Check if days in this group have differing schedules
          const displaySchedule = useGlobalDefaults ? globalSchedule : schedule;
          const groupHasMismatch = !isExpanded && group.days.length > 1 && displaySchedule &&
            group.days.some(d => {
              const daySchedule = displaySchedule[d] || emptyDaySchedule;
              const firstSchedule = displaySchedule[group.days[0]] || emptyDaySchedule;
              return !schedulesMatch(daySchedule, firstSchedule);
            });

          return (
            <ScheduleRow
              key={group.days.join('-')}
              days={group.days}
              schedule={group.schedule || emptyDaySchedule}
              globalSchedule={globalSchedule[group.days[0]] || group.schedule || emptyDaySchedule}
              isGhosted={false}
              isExpanded={isExpanded}
              onToggleExpand={() => setIsExpanded(!isExpanded)}
              onChange={(field, value) => handleScheduleChange(group.days, field, value)}
              onCopyToAll={() => handleCopyToAll(group.days)}
              canExpand={!isExpanded && group.days.length > 1}
              hasMismatch={groupHasMismatch}
            />
          );
        })}

        {/* Legend */}
        <Paper
          sx={{
            p: 2,
            mt: 2,
            bgcolor: colors.bgRecessed,
            borderRadius: 2,
            border: `1px solid ${colors.border}`,
          }}
        >
          <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 1 }}>
            <strong>How it works:</strong>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: colors.successGlow }} />
              <Typography variant="caption" sx={{ color: colors.textMuted }}>Store Opens</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: colors.errorGlow }} />
              <Typography variant="caption" sx={{ color: colors.textMuted }}>Store Closes</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: 0.5, bgcolor: colors.warning }} />
              <Typography variant="caption" sx={{ color: colors.textMuted }}>Liquor Sales Stop (before close)</Typography>
            </Box>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
}

export default SmartScheduleEditor;
